"use client";

import { useRef, useState } from "react";
import { prepareMediaUploadFile } from "@/lib/prepare-upload-file";
import { Button, Card, Spinner } from "./ui";

/**
 * 创作向导 Step 1 的商品素材区（可选）：
 * 照片会并入商品的 images（喂脚本 + 商品镜头素材），
 * 视频存作 Product Showcase 引擎的混剪素材。
 */

export interface ProductMediaItem {
  id: string;
  kind: "photo" | "video";
  url: string;
  filename: string;
}

export function ProductMedia({
  items,
  onChange,
  badge,
  onAnalyze,
  analyzing = false,
  analyzeDisabled = false,
  title = "Product photos & video",
  description =
    "Optional — your own shots beat scraped ones. Photos feed the script and product scenes; a short clip becomes footage for product-showcase videos.",
  allowPhotos = true,
  allowVideo = true,
  maxItems = 8,
}: {
  items: ProductMediaItem[];
  onChange: (items: ProductMediaItem[]) => void;
  /** 模块序号角标（向导 Step 1 的 ②） */
  badge?: string;
  onAnalyze?: () => void;
  analyzing?: boolean;
  analyzeDisabled?: boolean;
  title?: string;
  description?: string;
  allowPhotos?: boolean;
  allowVideo?: boolean;
  maxItems?: number;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    try {
      const created: ProductMediaItem[] = [];
      for (const file of Array.from(files)) {
        const compatibleFile = await prepareMediaUploadFile(file);
        const prepared = await prepareUpload("/api/products/uploads", compatibleFile);
        created.push(prepared);
      }
      const nextItems = [...items, ...created].slice(-maxItems);
      onChange(nextItems);
      const retainedIds = new Set(nextItems.map((item) => item.id));
      for (const displaced of items.filter((item) => !retainedIds.has(item.id))) {
        await fetch(`/api/products/uploads/${displaced.id}`, {
          method: "DELETE",
        }).catch(() => {});
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setBusy(false);
      if (photoInput.current) photoInput.current.value = "";
      if (videoInput.current) videoInput.current.value = "";
    }
  }

  async function remove(id: string) {
    onChange(items.filter((x) => x.id !== id));
    await fetch(`/api/products/uploads/${id}`, { method: "DELETE" }).catch(
      () => {},
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2.5">
        {badge && (
          <span className="grid size-6 place-items-center rounded-full bg-white/10 text-[11px] font-medium">
            {badge}
          </span>
        )}
        <span className="text-sm font-medium">{title}</span>
      </div>
      <p className="mt-1 text-[12px] leading-5 text-muted">
        {description}
      </p>

      <div className="mt-3 flex flex-wrap gap-2.5">
        <input
          ref={photoInput}
          type="file"
          accept="image/*,.heic,.heif"
          multiple
          hidden
          onChange={(e) => void upload(e.target.files)}
        />
        <input
          ref={videoInput}
          type="file"
          accept="video/*"
          hidden
          onChange={(e) => void upload(e.target.files)}
        />
        {allowPhotos && (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => photoInput.current?.click()}
          >
            {busy ? <Spinner /> : maxItems === 1 ? "＋ Choose photo" : "＋ Add photos"}
          </Button>
        )}
        {allowVideo && (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => videoInput.current?.click()}
          >
            {busy ? <Spinner /> : maxItems === 1 ? "＋ Choose video" : "＋ Add video"}
          </Button>
        )}
        <span className="self-center text-[11px] text-muted">
          {allowPhotos && "Photos ≤ 10MB (including iPhone HEIC/HEIF)"}
          {allowPhotos && allowVideo && " · "}
          {allowVideo && "video ≤ 200MB"}
        </span>
      </div>

      {error && <div className="mt-3 text-[13px] text-[#ffb3b3]">{error}</div>}

      {items.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-2.5 sm:grid-cols-6">
          {items.map((m) => (
            <div
              key={m.id}
              className="group relative overflow-hidden rounded-lg border border-line bg-white/[0.03]"
            >
              {m.kind === "photo" ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={m.url}
                  alt={m.filename}
                  className="h-20 w-full object-cover"
                />
              ) : (
                <video
                  src={m.url}
                  className="h-20 w-full bg-black object-cover"
                  muted
                  playsInline
                  preload="metadata"
                />
              )}
              <button
                type="button"
                onClick={() => void remove(m.id)}
                aria-label={`Remove ${m.filename}`}
                className="absolute top-1 right-1 grid size-5 place-items-center rounded-full bg-black/60 text-[10px] text-white/90 transition hover:bg-black/80"
              >
                ✕
              </button>
              {m.kind === "video" && (
                <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] text-white/85">
                  Video
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {onAnalyze && (
        <div className="mt-4 border-t border-line pt-4">
          <Button
            className={analyzing ? "llm-working disabled:opacity-100" : undefined}
            onClick={onAnalyze}
            disabled={busy || analyzing || analyzeDisabled || items.length === 0}
          >
            {analyzing ? (
              <>
                <Spinner /> Analyzing media…
              </>
            ) : (
              "Analyze photo & video →"
            )}
          </Button>
        </div>
      )}
    </Card>
  );
}

async function prepareUpload(endpoint: string, file: File): Promise<ProductMediaItem> {
  const r = await fetch(endpoint, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ filename: file.name, mime: file.type, size: file.size }),
  });
  if (r.status === 400 && (r.headers.get("content-type") ?? "").includes("application/json")) {
    return uploadThroughServer(endpoint, file);
  }
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? "Upload failed");
  }
  const prepared = (await r.json()) as { uploadUrl: string; item: ProductMediaItem };
  const put = await fetch(prepared.uploadUrl, { method: "PUT", headers: { "content-type": file.type }, body: file });
  if (!put.ok) {
    await fetch(`${endpoint}/${prepared.item.id}`, { method: "DELETE" }).catch(() => {});
    throw new Error("Direct upload to media storage failed");
  }
  const completed = await fetch(endpoint, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ action: "complete", id: prepared.item.id }),
  });
  if (!completed.ok) {
    const body = (await completed.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(body.error ?? "Uploaded file failed security verification");
  }
  return (await completed.json()) as ProductMediaItem;
}

async function uploadThroughServer(endpoint: string, file: File): Promise<ProductMediaItem> {
  const form = new FormData();
  form.append("files", file);
  const r = await fetch(endpoint, { method: "POST", body: form });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? "Upload failed");
  }
  return ((await r.json()) as ProductMediaItem[])[0];
}
